// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useDisplayCurrency } from '../../context/displayCurrencyContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  IconCheckmark,
  IconVerified,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';
import IconCollection from '../IconCollection/IconCollection';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @param {string?} props.imageRootClassName optional class for the image root (overrides default rootForImage)
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    imageRootClassName,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={imageRootClassName || css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {string?} props.variant use `verifiedCreatorCollection` for creator promo cards, or
 *   `profileProductGrid` for profile listing grid (image, category, title, price, Buy item CTA).
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const { displayCurrency, eurToUsdRate } = useDisplayCurrency();
  const displayCurrencyOptions = { displayCurrency, eurToUsdRate };

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    variant,
    thumbnailSrc,
    thumbnailImage,
    thumbnailAlt,
    verifiedLabel = 'VERIFIED',
    itemsCountText,
    categoryLabel,
    creatorName,
    creatorSubtitle,
    ctaText,
    ctaHref,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  if (variant === 'verifiedCreatorCollection') {
    const safeHref = ctaHref || '#';
    const categoryUpper = (categoryLabel || '').toUpperCase();
    const isInternalHref = typeof safeHref === 'string' && safeHref.startsWith('/');
    const LinkComponent = isInternalHref ? Link : 'a';
    const linkProps = isInternalHref ? { to: safeHref } : { href: safeHref };

    return (
      <LinkComponent className={classNames(classes, css.verifiedCreatorRoot)} {...linkProps}>
        <div className={css.verifiedCreatorMedia}>
          <AspectRatioWrapper className={css.verifiedCreatorAspect} width={317} height={421}>
            {thumbnailImage ? (
              <ResponsiveImage
                rootClassName={css.verifiedCreatorImage}
                alt={thumbnailAlt || creatorName || ''}
                image={thumbnailImage}
                variants={[
                  'similar-seller-card',
                  'similar-seller-card-2x',
                  'square-small',
                  'square-small2x',
                ]}
                sizes="(max-width: 767px) 50vw, 25vw"
              />
            ) : thumbnailSrc ? (
              <img className={css.verifiedCreatorImage} src={thumbnailSrc} alt="" />
            ) : (
              <div className={css.verifiedCreatorImageFallback} />
            )}
            <div className={css.verifiedCreatorBadge} aria-label={verifiedLabel}>
              <IconVerified />
            </div>
            {itemsCountText ? (
              <div className={css.verifiedCreatorItemsPill} aria-label={itemsCountText}>
                {itemsCountText}
              </div>
            ) : null}
          </AspectRatioWrapper>
        </div>

        <div className={css.verifiedCreatorMeta}>
          <div
            className={css.verifiedCreatorCategory}
            aria-hidden={!categoryUpper ? true : undefined}
          >
            {categoryUpper || '\u00a0'}
          </div>
          {creatorName ? <div className={css.verifiedCreatorName}>{creatorName}</div> : null}
          {creatorSubtitle ? (
            <div className={css.verifiedCreatorSubtitle}>{creatorSubtitle}</div>
          ) : null}
          <div className={css.verifiedCreatorCtaRow}>
            <span className={css.verifiedCreatorCta}>
              {ctaText || 'View Collection'}{' '}
              <span aria-hidden="true">
                <IconCollection name="arrowRight" />
              </span>
            </span>
          </div>
        </div>
      </LinkComponent>
    );
  }

  if (variant === 'profileProductGrid') {
    const translations = getListingCardTranslations(
      listing,
      config,
      intl,
      displayCurrencyOptions
    );
    const { titlePlain, cardAriaLabel, showPrice, priceTooltip, priceMessage } = translations;

    const listingUuid = listing?.id?.uuid;
    const { title = '', publicData } = listing?.attributes || {};
    const slug = createSlug(title);
    const { listingType, cardStyle } = publicData || {};
    const validListingTypes = config.listing.listingTypes || [];
    const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
    const showListingImage = requireListingImage(foundListingTypeConfig);

    const {
      aspectWidth = 317,
      aspectHeight = 421,
      variantPrefix = 'card-grid',
    } = config.layout.listingImage;

    const setActivePropsMaybe = setActiveListing
      ? {
          onMouseEnter: () => setActiveListing(listing?.id),
          onMouseLeave: () => setActiveListing(null),
        }
      : null;

    const buyItemLabel = intl.formatMessage({ id: 'ListingCard.buyItem' });

    const profileProductCard = (
      <>
        {showListingImage ? (
          <ListingCardImage
            renderSizes={renderSizes}
            title={titlePlain}
            listing={listing}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={aspectWidth}
            aspectHeight={aspectHeight}
            variantPrefix={variantPrefix}
            aspectRatioClassName={classNames(css.profileProductAspect, aspectRatioClassName)}
            lazyLoadImage={lazyLoadImage}
            imageRootClassName={css.profileProductRootForImage}
          />
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={classNames(css.profileProductAspect, aspectRatioClassName)}
            width={aspectWidth}
            height={aspectHeight}
            setActivePropsMaybe={setActivePropsMaybe}
          />
        )}

        <div className={css.profileProductMeta}>
          <div className={css.profileProductTitle}>{titlePlain}</div>
          {showPrice ? (
            <div className={css.profileProductPrice} title={priceTooltip}>
              {priceMessage}
            </div>
          ) : null}
          <div className={css.profileProductCta} aria-hidden="true">
            <span className={css.profileProductCtaLabel}>{buyItemLabel}</span>
            <span className={css.profileProductCtaIcon}>
              <IconCollection name="arrowRight" />
            </span>
          </div>
        </div>
      </>
    );

    const profileRootClass = classNames(classes, css.profileProductRoot);
    const profileAria = cardAriaLabel ? { 'aria-label': cardAriaLabel } : {};

    return listingUuid ? (
      <NamedLink
        className={profileRootClass}
        name="ListingPage"
        params={{ id: listingUuid, slug }}
        ariaLabel={cardAriaLabel}
      >
        {profileProductCard}
      </NamedLink>
    ) : (
      <div className={profileRootClass} role="group" {...profileAria}>
        {profileProductCard}
      </div>
    );
  }

  const translations = getListingCardTranslations(listing, config, intl, displayCurrencyOptions);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const defaultCardBody = (
    <>
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={aspectRatioClassName}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      <div className={css.info}>
        {showPrice ? (
          <div className={css.price} title={priceTooltip}>
            {priceMessage}
          </div>
        ) : null}
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
          {showAuthorInfo ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  const defaultAria = cardAriaLabel ? { 'aria-label': cardAriaLabel } : {};

  return id ? (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {defaultCardBody}
    </NamedLink>
  ) : (
    <div className={classes} role="group" {...defaultAria}>
      {defaultCardBody}
    </div>
  );
};

export default ListingCard;
